import {
  Box,
  Flex,
  Text,
  Badge,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Portal,
} from '@chakra-ui/react'
import { MoreVertical, Edit, Trash2, Star } from 'lucide-react'
import { formatIban } from '../utils/iban'
import {
  BANK_ACCOUNT_TYPE_LABELS,
  BANK_ACCOUNT_TYPE_COLORS,
  type BankAccount,
} from '../types'

interface BankAccountCardProps {
  account: BankAccount
  onEdit: (account: BankAccount) => void
  onDelete: (account: BankAccount) => void
}

export function BankAccountCard({ account, onEdit, onDelete }: BankAccountCardProps) {
  const typeLabel = BANK_ACCOUNT_TYPE_LABELS[account.account_type]
  const typeColor = BANK_ACCOUNT_TYPE_COLORS[account.account_type]

  return (
    <Box
      p={4}
      bg="white"
      borderWidth="2px"
      borderColor={`${typeColor}.200`}
      borderRadius="md"
      _hover={{ borderColor: `${typeColor}.300`, shadow: 'sm' }}
      position="relative"
      transition="all 0.2s"
    >
      {/* Tag type sur la bordure */}
      <Box
        position="absolute"
        top="-12px"
        left="16px"
        bg="white"
        px={3}
        py={1}
        borderRadius="md"
        borderWidth="2px"
        borderColor={`${typeColor}.200`}
      >
        <Text fontSize="xs" fontWeight="semibold" color={`${typeColor}.700`}>
          {typeLabel}
        </Text>
      </Box>

      <Flex justify="space-between" align="start" mb={3} mt={2}>
        <Flex direction="column" gap={1} flex={1}>
          <Flex align="center" gap={2}>
            <Text fontWeight="semibold" fontSize="md">
              {account.label}
            </Text>
            {account.is_primary && (
              <Badge colorPalette="yellow" size="sm" gap={1}>
                <Star size={12} />
                Principal
              </Badge>
            )}
          </Flex>
        </Flex>

        <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
          <MenuTrigger asChild>
            <IconButton size="sm" variant="ghost">
              <MoreVertical />
            </IconButton>
          </MenuTrigger>
          <Portal>
            <MenuPositioner>
              <MenuContent minW="160px">
                <MenuItem value="edit" onClick={() => onEdit(account)}>
                  <Edit /> Modifier
                </MenuItem>
                <MenuItem value="delete" color="red.500" onClick={() => onDelete(account)}>
                  <Trash2 /> Supprimer
                </MenuItem>
              </MenuContent>
            </MenuPositioner>
          </Portal>
        </MenuRoot>
      </Flex>

      <Flex direction="column" gap={2} fontSize="sm">
        <Flex justify="space-between">
          <Text color="fg.muted">IBAN</Text>
          <Text fontFamily="mono" fontSize="xs">
            {formatIban(account.iban)}
          </Text>
        </Flex>

        {account.bic && (
          <Flex justify="space-between">
            <Text color="fg.muted">BIC</Text>
            <Text fontFamily="mono" fontSize="xs">
              {account.bic}
            </Text>
          </Flex>
        )}

        <Flex justify="space-between">
          <Text color="fg.muted">Banque</Text>
          <Text>{account.bank_name}</Text>
        </Flex>

        <Flex justify="space-between">
          <Text color="fg.muted">Titulaire</Text>
          <Text>{account.account_holder}</Text>
        </Flex>

        {account.residence_chart_account && (
          <Flex justify="space-between">
            <Text color="fg.muted">Numéro de compte</Text>
            <Text fontFamily="mono" fontSize="xs" fontWeight="semibold">
              {account.residence_chart_account.custom_code}
            </Text>
          </Flex>
        )}

        {!account.is_active && (
          <Badge colorPalette="gray" size="sm" mt={2}>
            Compte désactivé
          </Badge>
        )}
      </Flex>
    </Box>
  )
}
